import { ActionContext } from 'vuex';
import dayjs from 'dayjs';
import { Nullable } from '@jamesgmarks/utilities';
import axios from 'axios';
import {
  Equal,
  GreaterThan,
  IQueryCommandOptions,
  In,
  LessThanOrEqual,
  Not,
  convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { StoreBaseImplementation } from '@/shared/classes/store-base-implementation';
import config from '@/config';
import { downloadFile } from '@/shared/helpers/download-file';
import { EFreshbooksState, EHydraState, IHydraInAppPaymentsInvoice } from '@/shared/interfaces/hydra-invoice';
import { isAxiosError } from '@/shared/types/type-guards/is-axios-error';
import { IStateStrict } from '@/shared/interfaces/store-base';
import { IPaymentPayload, isIPaymentPayload } from '@/shared/interfaces/in-app-payments';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

/**
 * keeps retrying a url until an expected result or an error is returned
 * @param url url to poll
 * @returns the result of poll if validation passes
 */
// eslint-disable-next-line consistent-return
const pollToApi = async (url: string) => {
  // eslint-disable-next-line no-promise-executor-return
  const sleep = async (delay: number) => (new Promise((resolve) => setTimeout(resolve, delay)));
  // eslint-disable-next-line no-constant-condition
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const axiosResponse = await axios.get(url);
      if (axiosResponse.status !== 202) {
        return axiosResponse;
      }
      // eslint-disable-next-line no-await-in-loop,
      await sleep(5000);
    }
  } catch (error) {
    if (!isAxiosError(error)) {
      throw new Error(`Polling stopped prematurely: ${JSON.stringify(error)}`);
    }
    throw new Error(error.response ? `${(error.response.data as { error?: string }).error}` : `${error}`);
  }
};

// Base store configuration
const adminGatewayResourcePath: string = '/billing-api/v1/hydra/invoices';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {};
const defaultGetSingleFilters: IQueryCommandOptions = {};

export type TPaymentConfirmation = 'ERR001' | 'ERR002' | 'ERR003' | 'ERR004' | 'ERR005' | 'SUCCESS' | 'OTHER';
export interface ICustomHydraInvoiceState extends IStateStrict<IHydraInAppPaymentsInvoice> {
  paymentConfirmationStatus: Nullable<TPaymentConfirmation>,
  totalOutstanding: number,
  allOutstandingInvoices: IHydraInAppPaymentsInvoice[],
}

const selectArray = [
  'id',
  'invoiceNumber',
  'invoiceDate',
  'amountInvoiced',
  'taxes',
  'currency',
  'ownershipGroupId',
  'freshbooksOutstandingAmount',
  'description',
];

/**
 * appends additional filters to must have (default) filters
 * @param customFilters IQueryCommandOptions to append to the default filters
 * @param freshbooksClientId freshbooks client id for which to retrieve invoices for
 * @returns IQueryCommandOptions
 */
const appendDefaultFiltersToCustomFilters = (
  customFilters: IQueryCommandOptions,
  freshbooksClientId: number,
) => {
  const filters: IQueryCommandOptions = {
    ...customFilters,
    alias: 'inv',
    select: [
      ...(customFilters.select ?? []),
      ...selectArray,
      'totalOutstandingAmount',
      'paid',
      'overdue',
      'unpaid',
    ],
    where: [
      ...(customFilters.where ?? []),
      {
        field: 'freshbooksState',
        operator: 'in',
        value: [EFreshbooksState.draft, EFreshbooksState['send-error']].join('|'),
        notOperator: true,
      },
      {
        field: 'hydraState',
        value: EHydraState.sent,
      },
      {
        field: 'freshbooksClientId',
        value: freshbooksClientId.toString(),
      },
    ],
    limit: customFilters.limit ?? 9999,
    aggregations: [
      {
        propertyAlias: 'totalOutstandingAmount',
        table: 'invoices',
        aggregate: 'SUM',
        column: 'freshbooksOutstandingAmount',
        whereCommands: [
          Not(Equal('freshbooksState', EFreshbooksState.draft)),
          Not(Equal('freshbooksState', EFreshbooksState['send-error'])),
          Equal('hydraState', EHydraState.sent),
          Equal('freshbooksClientId', freshbooksClientId),
          GreaterThan('freshbooksOutstandingAmount', '0'),
        ],
      },
      {
        propertyAlias: 'paid',
        table: 'invoices',
        aggregate: 'COUNT',
        column: 'freshbooksOutstandingAmount',
        whereCommands: [
          Equal('id', 'inv.id'),
          LessThanOrEqual('freshbooksOutstandingAmount', '0'),
        ],
      },
      {
        propertyAlias: 'overdue',
        table: 'invoices',
        aggregate: 'COUNT',
        column: 'freshbooksOutstandingAmount',
        whereCommands: [
          Equal('id', 'inv.id'),
          GreaterThan('freshbooksOutstandingAmount', '0'),
          LessThanOrEqual('invoiceDate', (dayjs().subtract(30, 'days').format('YYYY-MM-DD'))),
        ],
      },
      {
        propertyAlias: 'unpaid',
        table: 'invoices',
        aggregate: 'COUNT',
        column: 'freshbooksOutstandingAmount',
        whereCommands: [
          Equal('id', 'inv.id'),
          GreaterThan('freshbooksOutstandingAmount', '0'),
          GreaterThan('invoiceDate', (dayjs().subtract(30, 'days').format('YYYY-MM-DD'))),
        ],
      },
    ],
  };

  return filters;
};

const allInvoicesFilter = (freshbooksClientId: number) => (
  {
    select: selectArray,
    where: [
      Not(In('freshbooksState', [EFreshbooksState.draft, EFreshbooksState['send-error']])),
      Equal('hydraState', EHydraState.sent),
      Equal('freshbooksClientId', freshbooksClientId),
      GreaterThan('freshbooksOutstandingAmount', '0'),
    ],
    limit: 9999,
  });

const base = new StoreBaseImplementationV2<IHydraInAppPaymentsInvoice>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

export default {
  namespaced: true, // 'invoices/'

  state: {
    ...base.state,
    paymentConfirmationStatus: null,
    totalOutstanding: 0,
    allOutstandingInvoices: [],
  } as ICustomHydraInvoiceState,

  getters: {
    ...base.getters,
    paymentConfirmationStatus: (state: ICustomHydraInvoiceState) => state.paymentConfirmationStatus,
    totalOutstanding: (state: ICustomHydraInvoiceState) => state.totalOutstanding,
    allOutstandingInvoices: (state: ICustomHydraInvoiceState) => state.allOutstandingInvoices,
  },

  mutations: {
    ...base.mutations,

    setPaymentConfirmationStatus: (state: ICustomHydraInvoiceState, payload: Nullable<TPaymentConfirmation>) => {
      state.paymentConfirmationStatus = payload;
    },
    setTotalOutstanding: (state: ICustomHydraInvoiceState, payload: number) => {
      state.totalOutstanding = payload;
    },
    setAllOutstandingInvoices: (state: ICustomHydraInvoiceState, payload: IHydraInAppPaymentsInvoice[]) => {
      state.allOutstandingInvoices = payload;
    },
  },

  actions: {
    ...base.actions,

    /**
     * gets a list of invoices based on the provided filters
     * @param ctx
     * @param payload filters for the invoices
     */
    async get(ctx: ActionContext<ICustomHydraInvoiceState, any>, payload: IFilterPayloadV2) {
      const freshbooksId = ctx.rootGetters['users/currentClient'].freshbooksClientId;
      // Clients without a Freshbooks account have no invoices. Short-circuit to an empty list
      // rather than building a filter that calls `.toString()` on a null id (which crashes the page).
      if (freshbooksId == null) {
        ctx.commit('set', []);
        return;
      }
      const invoicesFilters = appendDefaultFiltersToCustomFilters(payload?.filters ?? {}, freshbooksId);
      const invoicesResult = await adminRequest.get<IHydraInAppPaymentsInvoice[]>(`${adminGatewayResourcePath}${convertQueryCommandToQueryString(invoicesFilters)}`);
      if (invoicesResult?.data && invoicesResult.data.length > 0 && invoicesResult.data[0].totalOutstandingAmount) {
        ctx.commit('setTotalOutstanding', invoicesResult.data[0].totalOutstandingAmount);
      }
      ctx.commit('set', invoicesResult);
    },

    async getAllOutstanding(ctx: ActionContext<ICustomHydraInvoiceState, any>) {
      const freshbooksId = ctx.rootGetters['users/currentClient'].freshbooksClientId;
      const invoicesFilters = allInvoicesFilter(freshbooksId);
      const invoicesResult = await adminRequest.get<IHydraInAppPaymentsInvoice[]>(`${adminGatewayResourcePath}${convertQueryCommandToQueryString(invoicesFilters)}`);
      if (invoicesResult?.data && invoicesResult.data.length > 0) {
        ctx.commit('setAllOutstandingInvoices', invoicesResult.data);
      }
    },

    /**
     * download an invoice as a pdf file
     * @param ctx
     * @param payload query string data required to download the invoice
     */
    async downloadInvoicePdf(ctx: ActionContext<any, any>, payload: {
      invoiceNumber: string,
      invoiceId: number,
      ownershipGroupName: string,
    }) {
      const { invoiceNumber, invoiceId, ownershipGroupName } = payload;
      const downloadFileResponse = await axios.get<ArrayBuffer>(
        `${config.adminGateway}/billing-api/downloads/generate_and_download_invoice_pdf`,
        {
          responseType: 'arraybuffer',
          params: { invoiceNumber, invoiceId },
        },
      );
      const fileName = `${invoiceNumber}${ownershipGroupName ? `#${ownershipGroupName}` : ''}.pdf`;
      const fileUrl = window.URL.createObjectURL(new Blob([downloadFileResponse.data]));
      downloadFile(fileUrl, `${fileName}`);
    },

    /**
     * sends a request for a session url based on the payment payload
     * @param ctx
     * @param payload IPaymentPayload. data for the payments to be made
     * @returns a session url
     */
    async getCheckoutSessionUrl(ctx: ActionContext<ICustomHydraInvoiceState, any>, payload: IPaymentPayload) {
      if (!isIPaymentPayload(payload)) {
        throw new Error('Payment payload is incorrect. Payload must have valid payment details (IPaymentDetail)');
      }
      const { data: sessionUrl } = await adminRequest.post(`/billing-api/payments/checkout-session`, payload);
      return sessionUrl;
    },

    /**
     * polls billing api to retrieve payment status
     * @param ctx
     * @param payload session id for the checkout session
     */
    async getPaymentConfirmation(ctx: ActionContext<ICustomHydraInvoiceState, any>, payload: {sessionId: string}) {
      const { sessionId } = payload;
      const initialTimeInMilliseconds = dayjs.utc().valueOf();
      if (!sessionId || sessionId.length === 0) {
        throw new Error('No session id provided');
      }
      const queryString = `?initialTimeInMilliseconds=${initialTimeInMilliseconds}&sessionId=${sessionId}`;
      const resourcePath = `/billing-api/payments/stripe-payment-confirmation`;
      const url = `${config.adminGateway}${resourcePath}${queryString}`;
      try {
        const result = await pollToApi(url);
        ctx.commit('setPaymentConfirmationStatus', 'SUCCESS');
      } catch (err) {
        console.error(err);
        const errorMessage = (err as Error).message;
        const paymentConfirmationError = errorMessage.match(/ERR{1}\d{3}/) ? errorMessage : 'OTHER';
        ctx.commit('setPaymentConfirmationStatus', paymentConfirmationError);
      }
    },
  },
};
